import { useState } from 'react';
import axios from 'axios';
import { Trash2, Loader2 } from 'lucide-react';
import { isAdmin } from '@/lib/adminAuth';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Admin-only floating delete button rendered as an overlay on any content
 * card (images, videos, polls, quotes — anything with an `id`). Renders
 * nothing for non-admins so the UI is identical for regular users.
 *
 * Props:
 *  - contentId: required, the content document's `id`
 *  - onDeleted (function): called after a successful delete so the parent
 *    can remove the item from local state without a full page refresh.
 *  - token (string, optional): explicit bearer token. Falls back to the
 *    cached `zenscroll_token` in localStorage when omitted.
 *  - className (string): wrapper positioning classes (defaults to top-right
 *    inside an `absolute` parent).
 *  - testId (string): suffix for the data-testid (e.g. `community-${id}`).
 */
export default function AdminDeleteButton({
  contentId,
  onDeleted,
  token,
  className = 'absolute top-2 right-2 z-20',
  testId,
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!isAdmin()) return null;

  const authToken = token || localStorage.getItem('zenscroll_token') || '';

  const doDelete = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/content/${contentId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      onDeleted?.(contentId);
    } catch (err) {
       
      alert(err?.response?.data?.detail || 'Failed to delete content');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <div
        data-testid={`admin-delete-confirm-${testId || contentId}`}
        className={`${className} flex items-center gap-1 rounded-full bg-black/85 backdrop-blur border border-red-400/50 px-2 py-1 text-[11px]`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white/90 px-1 hidden sm:inline">Delete this?</span>
        <button
          type="button"
          onClick={doDelete}
          disabled={busy}
          data-testid={`admin-delete-yes-${testId || contentId}`}
          className="px-2 py-0.5 rounded-full bg-red-500 text-white hover:bg-red-400 disabled:opacity-50 flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
          disabled={busy}
          data-testid={`admin-delete-cancel-${testId || contentId}`}
          className="px-2 py-0.5 rounded-full bg-white/10 text-white hover:bg-white/15 disabled:opacity-50"
        >
          No
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`admin-delete-btn-${testId || contentId}`}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setConfirming(true); }}
      title="Delete this content (admin)"
      className={`${className} inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/55 hover:bg-red-500/90 border border-white/10 hover:border-red-300/40 backdrop-blur text-white/85 hover:text-white transition-colors`}
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}
